import Typography, { TypographyProps } from "@mui/material/Typography";
import ErrorIcon from "@mui/icons-material/Error";
import { SvgIconProps } from "@mui/material/SvgIcon";
import { HTMLAttributes } from "react";
import Box from "@mui/material/Box";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { alpha, useTheme } from "@mui/material/styles";

export interface StoreErrorProps extends HTMLAttributes<HTMLElement> {
  errorMessage?: string;
  iconProps?: SvgIconProps;
  variant?: "medium" | "large";
  sx?: TypographyProps["sx"];
}

const fontSizeVariant = {
  medium: 14,
  large: 16,
};

export const StoreError = ({
  errorMessage,
  iconProps,
  variant = "medium",
  sx,
  ...props
}: StoreErrorProps) => {
  const fontSize = fontSizeVariant[variant];
  const theme = useTheme();
  const lineHeight = 1.5;
  if (!errorMessage) return null;
  return (
    <Typography
      {...props}
      sx={{
        color: theme.vars.palette.error.main,
        backgroundColor: theme.vars.palette.error.light,
        fontSize,
        marginTop: 2,
        marginBottom: 1,
        padding: "7px 12px 7px 9px",
        display: "flex",
        alignItems: "flex-start",
        fontWeight: 600,
        lineHeight,
        maxWidth: " 100%",
        width: "100%",
        boxSizing: "border-box",
        textAlign: "left",
        justifyContent: "flex-start",
        ...sx,
      }}
    >
      <Box
        component="span"
        display="flex"
        alignItems="center"
        height={`${lineHeight}em`}
      >
        <Box
          component="span"
          display="flex"
          alignItems="center"
          height={`${lineHeight}em`}
        >
          <ErrorIcon
            {...iconProps}
            sx={{ paddingRight: 0.75, fontSize: 18, ...iconProps?.sx }}
          />
        </Box>
      </Box>
      {errorMessage}
    </Typography>
  );
};

export const ErrorDot = ({ children }: { children: React.ReactNode }) => (
  <Box
    component="span"
    display="flex"
    alignItems="center"
    sx={(theme) => ({
      height: 20,
      width: 20,
      backgroundColor: theme.vars.palette.error.main,
      borderRadius: 999,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginRight: "8px",
      [theme.breakpoints.down("sm")]: {
        height: 12,
        width: 12,
        marginRight: "4px",
      },
    })}
  >
    {children}
  </Box>
);

export const StoreErrorV2 = ({
  errorMessage,
  iconProps,
  variant = "medium",
  sx,
  ...props
}: StoreErrorProps) => {
  const fontSize = fontSizeVariant[variant];
  const theme = useTheme();
  const lineHeight = 1.5;
  return !errorMessage ? null : (
    <Box
      {...props}
      sx={{
        color: "#ff534a",
        backgroundColor: alpha("#ff534a", 0.15),
        borderRadius: 999,
        fontSize,
        marginTop: 2,
        marginBottom: 1,
        padding: "7px 12px 7px 9px",
        display: "flex",
        alignItems: "center",
        fontWeight: 600,
        lineHeight,
        maxWidth: " 100%",
        width: "100%",
        boxSizing: "border-box",
        textAlign: "left",
        justifyContent: "flex-start",
        [theme.breakpoints.down("sm")]: {
          fontSize: fontSize - 2,

          padding: "4px 8px 4px 8px",
        },

        ...sx,
      }}
    >
      <ErrorDot>
        <CloseRoundedIcon
          {...iconProps}
          sx={{
            fontSize: 14,
            color: theme.vars.palette.error.contrastText,
            [theme.breakpoints.down("sm")]: {
              fontSize: 9,
            },
            ...iconProps?.sx,
          }}
        />
      </ErrorDot>
      {errorMessage}
    </Box>
  );
};
